import React, { createContext, useContext, useState, useCallback } from 'react';
import CustomAlert from '../components/CustomAlert';

// Create Alert Context
const AlertContext = createContext();

// Alert Provider Component
export const AlertProvider = ({ children }) => {
  const [alerts, setAlerts] = useState([]);

  // Hide specific alert
  const hideAlert = useCallback((id) => {
    setAlerts((prev) =>
      prev.map((alert) =>
        alert.id === id ? { ...alert, open: false } : alert
      )
    );

    // Remove from array after animation completes
    setTimeout(() => {
      setAlerts((prev) => prev.filter((alert) => alert.id !== id));
    }, 300);
  }, []);

  // Hide all alerts
  const hideAllAlerts = useCallback(() => {
    setAlerts((prev) =>
      prev.map((alert) => ({ ...alert, open: false }))
    );

    setTimeout(() => {
      setAlerts([]);
    }, 300);
  }, []);

  // Show alert function
  const showAlert = useCallback((options) => {
    const id = Date.now() + Math.random();
    const newAlert = {
      id,
      open: true,
      severity: options.severity || 'info',
      message: options.message || '',
      title: options.title || '',
      autoHideDuration: options.autoHideDuration !== undefined ? options.autoHideDuration : 4000,
      position: options.position || 'top-right',
      transition: options.transition || 'slide',
      variant: options.variant || 'filled',
      showIcon: options.showIcon !== undefined ? options.showIcon : true,
      closable: options.closable !== undefined ? options.closable : true,
      width: options.width || 'auto',
    };

    setAlerts((prev) => [...prev, newAlert]);

    // Auto remove after duration (if autoHideDuration is not null)
    if (newAlert.autoHideDuration !== null) {
      setTimeout(() => {
        hideAlert(id);
      }, newAlert.autoHideDuration + 300); // Extra time for transition
    }

    return id;
  }, [hideAlert]);



  // Convenience methods for different severity types
  const showSuccess = useCallback((message, options = {}) => {
    return showAlert({
      severity: 'success',
      message,
      ...options,
    });
  }, [showAlert]);

  const showError = useCallback((message, options = {}) => {
    return showAlert({
      severity: 'error',
      message,
      ...options,
    });
  }, [showAlert]);

  const showWarning = useCallback((message, options = {}) => {
    return showAlert({
      severity: 'warning',
      message,
      ...options,
    });
  }, [showAlert]);

  const showInfo = useCallback((message, options = {}) => {
    return showAlert({
      severity: 'info',
      message,
      ...options,
    });
  }, [showAlert]);

  const value = React.useMemo(() => ({
    showAlert,
    hideAlert,
    hideAllAlerts,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  }), [showAlert, hideAlert, hideAllAlerts, showSuccess, showError, showWarning, showInfo]);

  return (
    <AlertContext.Provider value={value}>
      {children}
      {/* Render all active alerts */}
      {alerts.map((alert) => (
        <CustomAlert
          key={alert.id}
          open={alert.open}
          onClose={() => hideAlert(alert.id)}
          severity={alert.severity}
          message={alert.message}
          title={alert.title}
          autoHideDuration={alert.autoHideDuration}
          position={alert.position}
          transition={alert.transition}
          variant={alert.variant}
          showIcon={alert.showIcon}
          closable={alert.closable}
          width={alert.width}
        />
      ))}
    </AlertContext.Provider>
  );
};

// Custom hook to use Alert
export const useAlert = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlert must be used within AlertProvider');
  }
  return context;
};

export default AlertContext;